/**
 * Mentions in chat (operator, 2026-10-22: "in chat system in any kind of chat
 * group or single use @ for mention person and # to mention task or ticket").
 *
 * Typing "@" in the message box offers people, "#" tasks and tickets; the one
 * picked goes into the words by its name — "@Mona Ali", "#TKT-2026-000123" —
 * and the box keeps where each such name is while the rest is written around it.
 * The message carries them as `mentions`: each one's place in its words (offset
 * and length in UTF-16 units — what a JavaScript string counts, as the API does)
 * and what it names. Plain functions over plain data; the page only draws them.
 */

/** What a mention names: someone (@), a task or a ticket (#). */
export type MentionType = 'USER' | 'TASK' | 'TICKET';

/** Where a mention is in some words, and what it names. */
export interface Placed {
  type: MentionType;
  id: string;
  offset: number;
  length: number;
}

/** The "@" or "#" being typed: its sign, where it is, and the words after it up to the caret. */
export interface Trigger {
  sign: '@' | '#';
  start: number;
  query: string;
}

/** As far back as a name being typed reaches from the caret — longer than any name. */
const REACH = 40;

/** What may stand before a sign that starts a mention: nothing, a blank, or an opening bracket or quote. */
const BEFORE_SIGN = /[\s([{"'«“]/u;

/** The one edit between two versions of the words: where it starts, and where it ends in each. */
function editBetween(before: string, after: string): { start: number; endBefore: number; endAfter: number } {
  let start = 0;
  const shorter = Math.min(before.length, after.length);
  while (start < shorter && before[start] === after[start]) start++;
  let endBefore = before.length;
  let endAfter = after.length;
  while (endBefore > start && endAfter > start && before[endBefore - 1] === after[endAfter - 1]) {
    endBefore--;
    endAfter--;
  }
  return { start, endBefore, endAfter };
}

/**
 * The mentions after the words changed from `before` to `after` — one edit, as
 * typing, deleting or pasting makes: those before it stay, those after it move
 * with it, and one it reached into is a mention no more (its words stay).
 */
export function followEdit<M extends Placed>(mentions: readonly M[], before: string, after: string): M[] {
  if (before === after || mentions.length === 0) return [...mentions];
  const { start, endBefore } = editBetween(before, after);
  const moved = after.length - before.length;
  return mentions.flatMap((m) => {
    if (m.offset + m.length <= start) return [m];
    if (m.offset >= endBefore) return [{ ...m, offset: m.offset + moved }];
    return [];
  });
}

/**
 * A deletion that reaches into a mention takes the whole of it, as WhatsApp's
 * box does: one Backspace after "@Mona Ali" leaves nothing of it. The words that
 * are left and where the caret goes — or null when the edit deleted no part of
 * a mention, or wrote something in its place.
 */
export function deleteWhole(
  mentions: readonly Placed[],
  before: string,
  after: string,
): { text: string; caret: number } | null {
  if (after.length >= before.length) return null;
  const { start, endBefore, endAfter } = editBetween(before, after);
  // Something written in the place of what went: a replacement, not a deletion.
  if (endAfter !== start) return null;
  let from = start;
  let to = endBefore;
  let reached = false;
  for (const m of mentions) {
    const end = m.offset + m.length;
    if (end > start && m.offset < endBefore) {
      reached = true;
      from = Math.min(from, m.offset);
      to = Math.max(to, end);
    }
  }
  if (!reached) return null;
  return { text: before.slice(0, from) + before.slice(to), caret: from };
}

/**
 * The "@" or "#" being typed at the caret, if any: a sign that starts a word —
 * at the start, or after a blank or an opening bracket — on the caret's line,
 * not part of a mention already made, with no blank right after it and at most
 * three blanks up to the caret (a name, not a sentence).
 */
export function activeTrigger(text: string, caret: number, mentions: readonly Placed[]): Trigger | null {
  const from = Math.max(0, caret - REACH - 1);
  for (let i = caret - 1; i >= from; i--) {
    const c = text[i];
    if (c === '\n') return null;
    if (c !== '@' && c !== '#') continue;
    if (i > 0 && !BEFORE_SIGN.test(text[i - 1])) return null;
    if (mentions.some((m) => i >= m.offset && i < m.offset + m.length)) return null;
    const query = text.slice(i + 1, caret);
    if (/^\s/u.test(query) || (query.match(/\s/gu)?.length ?? 0) > 3) return null;
    return { sign: c, start: i, query };
  }
  return null;
}

/**
 * The words with a picked name in place of the "@" or "#" being typed, a blank
 * after it (unless one follows already), the mentions moved to suit — the new
 * one among them — and the caret after it all.
 */
export function insertMention<M extends Placed>(
  text: string,
  mentions: readonly M[],
  trigger: Trigger,
  caret: number,
  label: string,
  named: Omit<M, 'offset' | 'length'>,
): { text: string; mentions: M[]; caret: number } {
  const rest = text.slice(caret);
  const gap = /^\s/u.test(rest) ? '' : ' ';
  const next = text.slice(0, trigger.start) + label + gap + rest;
  const placed = { ...named, offset: trigger.start, length: label.length } as M;
  const kept = followEdit(mentions, text, next).filter((m) => m.offset + m.length <= trigger.start || m.offset >= trigger.start + label.length);
  return {
    text: next,
    mentions: [...kept, placed].sort((a, b) => a.offset - b.offset),
    caret: trigger.start + label.length + 1,
  };
}

/**
 * What is sent of the words and their mentions: the words without the blanks
 * around them — the API keeps them so — and each mention moved back by the
 * blanks that went from the front. One no longer whole in them is left out.
 */
export function trimWithMentions<M extends Placed>(text: string, mentions: readonly M[]): { words: string; mentions: M[] } {
  const lead = text.length - text.trimStart().length;
  const words = text.trim();
  return {
    words,
    mentions: mentions
      .filter((m) => m.offset >= lead && m.offset + m.length <= lead + words.length && text.slice(m.offset, m.offset + 1) === (m.type === 'USER' ? '@' : '#'))
      .map((m) => ({ ...m, offset: m.offset - lead })),
  };
}

/**
 * A message's words in pieces: plain words, and each mention's — in order. A
 * mention that does not fit the words (an API of tomorrow, a slip) is left as
 * words.
 */
export function splitMentions<M extends Placed>(
  text: string,
  mentions: readonly M[] | null | undefined,
): Array<{ text: string; mention: M | null }> {
  const pieces: Array<{ text: string; mention: M | null }> = [];
  let at = 0;
  for (const m of [...(mentions ?? [])].sort((a, b) => a.offset - b.offset)) {
    if (m.offset < at || m.length < 2 || m.offset + m.length > text.length) continue;
    if (m.offset > at) pieces.push({ text: text.slice(at, m.offset), mention: null });
    pieces.push({ text: text.slice(m.offset, m.offset + m.length), mention: m });
    at = m.offset + m.length;
  }
  if (at < text.length) pieces.push({ text: text.slice(at), mention: null });
  return pieces;
}
